/* Deck content for the hidden /presentation route.
   Source of truth: docs/portfolio-tech-inventory.md (Part I narratives, Part II evidence).
   Every metric here is defensible from that document — do not add a number that is not in it. */

export type Metric = { value: string; label: string };

export type ProjectSlide = {
  kind: 'project';
  id: string;
  index: number;
  name: string;
  category: string;
  hue: string;
  oneLiner: string;
  pitch: string;
  features: string[];
  metrics: Metric[];
  stage: string;
  stack: string[];
  straight: string;
  live?: string;
  /* Short positioning kicker shown in the slide eyebrow. */
  hook?: string;
  /* One sentence that makes the hook concrete, shown under the one-liner. */
  hookNote?: string;
};

export type Slide =
  | { kind: 'cover' }
  | { kind: 'map' }
  | { kind: 'theses' }
  | { kind: 'clusters' }
  | ProjectSlide
  | { kind: 'numbers' }
  | { kind: 'risks' }
  | { kind: 'close' };

export const HUE = {
  infra: '#38bdf8',
  platform: '#fbbf24',
  vertical: '#a78bfa',
  media: '#fb7185',
  venture: '#34d399',
  client: '#94a3b8',
};

const projects: Omit<ProjectSlide, 'kind' | 'index'>[] = [
  {
    /* Copy follows specs/design2/MARKETING-STRATEGY.md — its headline, message hierarchy,
       approved claims and product vocabulary. Its "claims to avoid" list rules out gate counts
       as a quality proxy, HAZOP/Interrogator, Composer, and autonomous connector behaviour
       without configured credentials. Do not reintroduce them here. */
    id: 'autoarchitect',
    name: 'AutoArchitect',
    category: 'Platform · open-core',
    hue: HUE.platform,
    live: 'autoarchitect.ai',
    hook: 'Build the company, not the demo',
    oneLiner:
      'Turn a specification into a production-ready platform with autonomous workflows and coordinated AI agent teams built in.',
    pitch:
      'AutoArchitect is a specification compiler and proof harness. It does not stop at an attractive prototype: it models the business, generates the platform, populates it, exercises its promises in a browser, and releases only converged applications. Describe the operation once — AutoArchitect models, builds and proves it as one system, and you can inspect every stage and every proof receipt.',
    features: [
      'One semantic source — a single typed graph compiles the database, API, authorization, workflows, agents, connectors, UI, fixtures, tests and acceptance setup',
      'That removes the handoff gaps that make AI-generated applications look coherent while behaving inconsistently',
      'Semantic Closure before generation — guards, tests, seeds and acceptance plans are reconciled and the owning branch repaired, while contradictions are still cheap and explainable',
      'Production evidence, not confidence scores — Proof executes graph, API, workflow, populated-render, responsive and EARS browser evidence, and Release is backed by receipts you can inspect and replay',
      'Built-in autonomous operations — workflows, approvals, connectors, capabilities and AI teams are part of the specification and the runtime, not mock cards added after the app is drawn',
      'Rich deterministic presentation — the same graph selects dashboards, galleries, boards, maps, timelines, approvals, themes and responsive behaviour, without creating a second source of truth',
    ],
    metrics: [
      { value: '1', label: 'semantic graph behind app, workflows, agents and evidence' },
      { value: '6', label: 'Proof evidence lanes, replayable as receipts' },
      { value: '259', label: 'connector catalogue' },
      { value: 'Open core', label: 'local-first engine + optional commercial control plane' },
    ],
    stage: 'Late alpha · pre-first-public-release · website live',
    stack: ['TypeScript 5.7', 'Node 20 ESM', 'React 18 + Vite 6', 'Zod 4', 'Postgres + SQLite', 'K8s + gVisor', 'GHCR + cosign'],
    straight:
      'v0.1.0-alpha, and the marketing strategy is deliberate about what we will not claim: no guaranteed-perfect software, no gate count standing in for quality, and no autonomous external connector behaviour without configured credentials. Benchmarks stay unpublished until the numbers are real.',
  },
  {
    id: 'lumha-events',
    name: 'Lumha Events',
    category: 'Media AI · events',
    hue: HUE.media,
    oneLiner: 'One link runs the whole event — invitation, door, photos, albums, reels, print shop.',
    pitch:
      'Event tooling is five products stapled together and none of them know each other. Lumha is one link and one pipeline: the ticket that got someone through the door is the same identity that puts their photos in their album, and a single consent choke point decides what may ever be published.',
    features: [
      'One link covering invitation, RSVP, ticketing, door check-in, capture, albums, booths, reels and print',
      'Single consent choke point canPublish(asset, surface), retroactive opt-out sweeps, face vectors deleted 30 days post-event',
      'Signed one-time HMAC QR tickets with duplicate-scan blocking',
      '16 MCP tools over the HTTP API — the entire pipeline is agent-drivable and every call journaled',
      'Zero-setup demo: Postgres + pgvector in-process via PGlite WASM',
      'Deterministic mock inference behind a real provider seam — the GPU provider is a drop-in',
      'Licence-clean CV by deliberate selection: RT-DETR over AGPL YOLO, AuraFace over InsightFace, BiRefNet MIT',
    ],
    metrics: [
      { value: '145/145', label: 'end-to-end scenarios passing' },
      { value: '16', label: 'MCP tools' },
      { value: '30 days', label: 'face-vector hard deletion' },
    ],
    stage: 'Demo-complete alpha',
    stack: ['Fastify 5 + Drizzle', 'React 19 + Vite 7', 'Python FastAPI', 'SigLIP-2', 'AuraFace', 'PGlite', 'MCP'],
    straight:
      'No CI, no app Dockerfiles, dev secrets committed, and the core loop currently ships the deterministic mock inference provider. The seam is the mitigation, not the excuse.',
  },
  {
    id: 'zakhu',
    name: 'Zakhu — Brokerage OS',
    category: 'Vertical AI · real estate',
    hue: HUE.vertical,
    oneLiner:
      'An operating system for real-estate agents: a swarm of agents does the work, and only judgment calls reach the human.',
    pitch:
      'An agent’s day is 80% response latency and paperwork, 20% judgment. Zakhu automates the 80% and hands back the 20% as a queue of decisions with the context already assembled. The interesting engineering: the same knowledge base powers a private internal assistant and a public Persona Twin — and the scope split is enforced at retrieval, not by asking the model nicely.',
    features: [
      'Agent swarm with a human-in-the-loop “Close the loop” decision-card queue',
      'Retrieval-time scope enforcement separating the private assistant from the public Persona Twin',
      'Never-fail AI: deterministic cited stub → model refill → graceful fallback on any error',
      'Four-provider model fallback chain over a self-written OpenAI-compatible transport',
      'Hybrid NL portal search: LLM filter extraction → SQL prefilter → RAG re-rank → cited summary, with editable chips',
      'Behavioural lead engine: dwell/event signals → intent score → routing → priced lead ledger',
      'Full MLS graph with CREA DDF / realtor.ca import; complete three-tier Stripe subscription state machine',
    ],
    metrics: [
      { value: '3-sided', label: 'consumer · agent · brokerage marketplace' },
      { value: '4', label: 'LLM providers in the fallback chain' },
      { value: '$0/29/79', label: 'Scout / Solo / Pro tiers, wired' },
    ],
    stage: 'Late prototype · pre-launch alpha',
    stack: ['Vite 7 + React 19', 'FastAPI', 'SQLAlchemy 2', 'glm-5.2 → minimax → deepseek → qwen', 'Stripe', 'Sentry'],
    straight:
      'Localhost API URL, SQLite dev DB, Stripe in test mode, some views still on mock data, and service keys shared with Lumha pending rotation before any public launch.',
  },
  {
    id: 'zakhusign',
    name: 'ZakhuSign — E-signature',
    category: 'Vertical AI · agreements',
    hue: HUE.vertical,
    oneLiner: 'E-signature where the AI fills, audits and routes the packet — and every signature is cryptographically sealed.',
    pitch:
      'DocuSign made signing electronic; it did not make the agreement intelligent. ZakhuSign reads your licensed forms once, learns where every field lives — including the dotted blanks on a flat scanned OREA form — then fills them from the deal, flags what a coordinator would catch, chases the laggards, seals the result to a long-term-validation standard, and gives every signer a permanent free vault.',
    features: [
      'AI packet build → autofill with per-field provenance and confidence → exception audit → routing → ceremony → seal → vault',
      'PAdES B-B / B-LT sealing with RFC 3161 timestamps, platform CA, and a public /verify portal',
      'Hash-chained audit log with a daily Merkle anchor — tamper evidence that survives the vendor',
      'Never ships licensed IP: the operator uploads their OREA/TRREB PDFs and the platform learns the field map',
      'Deterministic-first AI — a rules engine is the floor, Anthropic or any OpenAI-compatible endpoint is the upgrade',
      'Agreement-as-API: /extract, /autofill, /diff, /audit, terms objects; HMAC-SHA256 signed webhooks',
      'Free signer vault with magic-link access — the network-effect play against per-seat incumbents',
    ],
    metrics: [
      { value: '6', label: 'persona shells, agent → signer' },
      { value: 'PAdES', label: 'B-B upgradeable to B-LT' },
      { value: 'E2E', label: 'seeded deals run through the real pipeline' },
    ],
    stage: 'Demo-complete alpha · production-mode boot refusals enforced',
    stack: ['React 19 + Vite', 'FastAPI', 'pyHanko', 'Postgres 16', 'argon2 + JWT', 'Stripe', 'Turnstile'],
    straight:
      'No CI, not yet a git repository. RON, KBA/ID vendors, HSM keys, native apps and MLS/CRM connectors are deliberately deferred and listed as deferred.',
  },
  {
    id: 'lumha-creator',
    name: 'Lumha Creator',
    category: 'Consumer SaaS · live',
    hue: HUE.media,
    live: 'lumha.com',
    oneLiner: 'From expertise to a published, branded course — with the AI doing the production work.',
    pitch:
      'Course creators spend weeks on production and minutes on the thing they are actually expert in. Lumha inverts that: the studio drafts the curriculum, the lessons and the assets, and the creator edits rather than authors. It runs on the same Penny platform as our legal and career products — and it is already live.',
    features: [
      'Branded creator storefronts',
      'AI Course Studio: structure, content and asset drafting',
      'Publishing workflow from draft to live course',
      'Shared Penny runtime — RAG, agents, document processing',
      'faster-whisper ASR and TTS for audio lessons',
      'Stripe monetization',
    ],
    metrics: [
      { value: 'LIVE', label: 'lumha.com, in beta with real creators' },
      { value: '1', label: 'of 7 products on the shared runtime' },
    ],
    stage: 'Beta · live',
    stack: ['React', 'FastAPI', 'PydanticAI', 'faster-whisper', 'Stripe'],
    straight:
      'Distinct product from Lumha Events despite the shared name; a rename decision is pending. Catalogued through the Penny umbrella rather than surveyed directly.',
  },
  {
    id: 'aiportal',
    name: 'AI Portal',
    category: 'Platform · regulated verticals',
    hue: HUE.platform,
    oneLiner:
      'A knowledge assistant a law firm can actually put in front of a client — with citations, isolation and a published hallucination number.',
    pitch:
      'Legal AI has a credibility problem with a number attached — Stanford HAI measured how often the commercial tools hallucinate. We benchmark against those numbers, publish ours, and gate releases on them. Isolation is enforced by Postgres row-level security and tested at the SQL, retrieval and API layers. And it runs on self-hosted inference, so a Canadian firm’s documents never leave the country.',
    features: [
      'Database-enforced multi-tenancy: Postgres RLS via a non-superuser app role, tested at three layers',
      'Hybrid RRF retrieval + contextual chunks + cross-encoder rerank, with graceful degradation',
      'Citations to page and character offset; abstain on low confidence rather than guess',
      'Post-generation groundedness verifier running on a different model family than the generator',
      'Reproducible eval harness with release gates: hallucination ≤2%, R-Acc ≥95%, answerable ≥90%',
      'Manifest-driven vertical plugins — agent templates, tools, skills and seed corpora per practice area',
    ],
    metrics: [
      { value: '≤2%', label: 'hallucination release gate' },
      { value: '0/10', label: 'prompt injections obeyed' },
      { value: '89/89', label: 'tasks closed' },
      { value: '9', label: 'recorded eval runs' },
    ],
    stage: 'Late alpha · demo-ready with a working Docker stack',
    stack: ['Python 3.12', 'FastAPI', 'PydanticAI', 'pgvector + RRF', 'bge-reranker', 'gpt-oss:120b (self-hosted)'],
    straight:
      'v0.1.0, no CI, single branch, no named customers. docs/soc2-readiness.md is a gap checklist, not a certification.',
  },
  {
    id: 'lantern',
    name: 'Lantern — Search without keys',
    category: 'Agent infrastructure',
    hue: HUE.infra,
    hookNote:
      'No API key, no per-request bill, and no single upstream that can rate-limit you out of business — SearXNG, a local index and site-search sit behind one interface, so a dead provider degrades instead of failing. The queries never leave your machine.',
    oneLiner: 'Firecrawl-class capability — search, crawl, markdown, extract — that you own end to end.',
    pitch:
      'Every RAG pipeline needs the live web, and today that means renting it: per-request pricing, API keys, usage caps, and your queries leaving the building. The self-hosted alternative is AGPL, which most companies cannot ship. Lantern is MIT, runs on one machine, and treats discovery as a pluggable interface so no upstream is load-bearing.',
    features: [
      'Five operations: search, fetch, bounded crawl, deep-search (search + read), structured extraction',
      'Pluggable SearchProvider registry with graceful fallback — SearXNG over HTTP, local index, site-search, meta',
      'Crawl4AI acquisition: Playwright or plain HTTP, markdown generation, CSS/XPath extraction, PDF text',
      'Licence discipline as a build gate — MIT/BSD/Apache only in-tree; copyleft only as a separate service over HTTP',
      'Ships an MCP server alongside the REST API — a tool endpoint for any agent framework on day one',
      'Production hygiene in v1 scope: auth, SSRF protection, robots.txt politeness, per-domain throttling, Prometheus metrics',
    ],
    metrics: [
      { value: 'MIT', label: 'vs the AGPL incumbent' },
      { value: '0', label: 'mandatory third-party API keys' },
      { value: '3+', label: 'discovery providers behind one interface' },
      { value: '1', label: 'machine, docker compose up' },
    ],
    stage: 'Alpha · full module structure · thin test suite',
    stack: ['Python 3.12', 'FastAPI', 'Crawl4AI', 'Playwright', 'SQLite / aiosqlite', 'MCP', 'Prometheus'],
    straight:
      'Earliest-stage tree in the portfolio: modules complete, tests thin, CI scaffolded but empty. Anti-bot defeat is explicitly out of scope and documented as a limitation.',
  },
  {
    id: 'capsule',
    name: 'Capsule — Green tokens',
    category: 'Agent infrastructure',
    hue: HUE.infra,
    hookNote:
      'Compile once per version instead of once per agent, per call. On Stripe, GitHub and Twilio that is 85% fewer tokens to a correct first call, an index costing ~1% of the bundle, and a Stripe charge that renders at 28 tokens instead of 256.',
    oneLiner:
      'Compile an API once per version into a signed artifact agents can actually use — instead of every agent re-reading the docs.',
    pitch:
      'Today every agent that touches an API compresses that API’s documentation by hand, independently, thousands of times a day — and each copy rots on its own schedule. Capsule does that compression once per version and ships the result as a verifiable artifact. MCP is the transport; a bundle is what should be on the wire.',
    features: [
      'One compiler, three protocols: OpenAPI 3.x, GraphQL introspection, AsyncAPI 2.x/3.x → one bundle shape',
      'Extracts what actually breaks agents: required_when lifted from prose, units, error taxonomy with retry semantics, idempotency, side effects',
      'Tiered index — browse 110 Stripe groups for ~4.5k tokens; load one operation for a few hundred, not 462k',
      'Three-grade verification: structural, semantic (every claim carries a cite pointer), live sandbox probes',
      'ed25519-signed, content-addressed, with a reproducible | attested determinism record',
      'Emitters as pure functions: MCP server, skill, typed SDK, raw tool schemas. capsule diff answers “what broke?”',
    ],
    metrics: [
      { value: '2,007', label: 'operations compiled, zero id collisions' },
      { value: '85.1%', label: 'cold token reduction (gate was 50%)' },
      { value: '5.7–7.7', label: 'index tokens per operation' },
      { value: '46 → 6', label: 'first-call hazards' },
    ],
    stage: 'Working core · Phases A–E complete · 10 test suites',
    stack: ['TypeScript / Node 22', 'Apache-2.0', 'ajv', 'js-tiktoken', 'ed25519', 'Zero runtime deps beyond 4'],
    straight:
      'Cold, for a single operation, a perfect retriever beats the bundle (943 vs 568 tokens) — the index is a fixed cost that earns back by operation two. We report that rather than bury it.',
  },
  {
    id: 'lugash',
    name: 'Lugash — Marketing OS',
    category: 'Vertical AI · marketing',
    hue: HUE.vertical,
    oneLiner: 'A marketing platform that watches how your creative travels through the world, and proves what it caused.',
    pitch:
      'Marketing runs on four disconnected tool silos and nobody owns the loop between them, so campaigns launch blind, earned propagation is invisible, and results get reported as correlations dressed as causes. Lugash runs one continuous loop over a knowledge graph that remembers. Agents commoditize; memory compounds.',
    features: [
      'The Loop: SENSE → UNDERSTAND → DECIDE → CREATE → ACTIVATE → OBSERVE → PROVE → LEARN → ADAPT, running continuously and broadcast live',
      'Propagation Graph — creative fingerprinting → pickup detection → influencer amplification → measured lift, in one loop',
      'Proof Engine that never conflates observed / attributed / incremental, backed by an Evidence Ledger',
      'Persistent World Model: typed, timestamped, evidence-linked knowledge graph with per-write provenance',
      'Method transparency — every LLM-derived field reports llm | heuristic; connector credential state is visible',
      'Human approval gates designed into the agent organization; explicit ethical non-goals enforced by a claims-checker agent',
    ],
    metrics: [
      { value: '9', label: 'loop phases, running in-process' },
      { value: '6', label: 'product surfaces on real endpoints' },
      { value: '17+', label: 'World Model entity types' },
    ],
    stage: 'Working alpha · seeded multi-persona tenant',
    stack: ['Vite 8 + React 19', 'FastAPI', 'Postgres 16 + pgvector', 'claude-opus-5 (optional)', 'imagehash + CLIP/MiniLM', 'WebSocket'],
    straight:
      'Working alpha. Real connector fleet, identity resolution and incrementality at scale are Phase 2+ and named as such. Not a git repository yet.',
  },
  {
    id: 'playback',
    name: 'Klipki — Playback',
    category: 'Media AI · sports',
    hue: HUE.media,
    oneLiner:
      'Upload the footage from a match, get back a searchable intelligence package — timeline, identities, stats, clips, reels and an API over all of it.',
    pitch:
      'Sports video AI usually means a black box that spits out highlights and asks you to trust it. Playback builds a structured event graph first, and everything the product shows derives from that graph, never from the pixels. Confidence is always shown, disagreement between sources surfaces, and a metric we cannot calibrate is withheld with a reason rather than estimated.',
    features: [
      'Sports Event Graph: one contract for API, workers and UI, with confidence, evidence links and model provenance on every object',
      'Eleven idempotent, resumable stages — the API owns the state machine, the worker owns the work',
      'Sport Packs: a new sport is a YAML directory. Basketball exists to prove it — zero core-code change',
      'Cost cascade to ≤$2.50/game-hour: one 720p proxy, adaptive frame rate, region-crop second pass, VLM on candidates only',
      'Structural tenancy — cross-tenant reads return 404, never 403, because 403 confirms the row exists',
      'Consent snapshotted at edit time, so revocation stops future training examples without relabelling history',
      'The EDL is the product and the MP4 is a cache of it — outputs reproducible from (edl_version, renderer_version)',
    ],
    metrics: [
      { value: '≤$2.50', label: 'per game-hour, engineered not hoped' },
      { value: '11', label: 'pipeline stages, resumable' },
      { value: '2', label: 'sports shipped, zero core changes for the 2nd' },
      { value: '1st', label: 'CI job is the licence allowlist, alone' },
    ],
    stage: 'Advanced alpha · real CI · models wired, not yet sport-trained',
    stack: ['Fastify + Drizzle', 'React 19', 'FastAPI worker', 'RF-DETR', 'ByteTrack', 'PaddleOCR', 'Qwen3-VL-8B 4-bit', 'Unsloth core'],
    straight:
      'Published in the README under “What is not built”: no trained sport heads (so confidences correctly sit below review thresholds), no Stripe yet, no annotation console.',
  },
  {
    id: 'careerbeyond',
    name: 'CareerBeyond AI',
    category: 'Vertical AI · careers',
    hue: HUE.vertical,
    oneLiner: 'Résumé, ATS match, mock interview and a mentor that remembers — one career platform.',
    pitch:
      'Career tools are point solutions — a résumé builder here, an interview app there, and none of them know your history. CareerBeyond runs on a platform with memory: the résumé, the ATS gap analysis, the interview feedback and the mentor conversation are all the same agent looking at the same candidate.',
    features: [
      'End-to-end résumé pipeline with a Fabric.js multi-page WYSIWYG template builder, exporting PDF and DOCX',
      'ATS matching and gap analysis',
      'Mock interview generation with structured feedback',
      'Persona mentor agents — Aura, Neo, Sage',
      'Career journeys, courses shared with LearningBeyond, and job bidding',
      'Job-board integrations: LinkedIn, Adzuna, Indeed, JSearch, Greenhouse, Lever',
    ],
    metrics: [
      { value: '60+', label: 'end-to-end test scripts' },
      { value: '6', label: 'job-board integrations' },
    ],
    stage: 'Beta · near-production',
    stack: ['FastAPI', 'PydanticAI', 'React 19', 'Fabric.js 6', 'WeasyPrint + ReportLab', 'Postgres'],
    straight:
      'Beta, near-production but not deployed. Three generations of this codebase exist in the tree; consolidation is outstanding.',
  },
  {
    id: 'lensevents',
    name: 'LensEvents AI',
    category: 'Media AI · in production',
    hue: HUE.media,
    live: 'deployed on AWS',
    oneLiner: 'The only thing in the portfolio already running in production on AWS.',
    pitch:
      'Photographers and event companies drown in unsorted media. LensEvents tags it, recognizes and clusters the faces, and produces the highlights — with per-guest privacy controls, because the fastest way to lose this market is to be careless with faces. It is deployed, monitored, and running on managed cloud infrastructure.',
    features: [
      'Vision tagging and automatic highlight generation',
      'Face recognition and clustering with per-guest privacy controls',
      'AI creative director',
      'Async Celery job system with per-queue retry dashboards',
      'Production AWS deployment: RDS Postgres + pgvector, S3 media, production compose, deployment runbook',
    ],
    metrics: [
      { value: 'PROD', label: 'AWS RDS + S3, deployed' },
      { value: '4', label: 'dedicated Celery queues with beat + auto-retry' },
    ],
    stage: 'Production-deployed',
    stack: ['FastAPI', 'InsightFace + onnxruntime', 'scikit-learn', 'Celery + Redis', 'AWS RDS + S3'],
    straight: 'Surveyed through the Penny umbrella; a direct diligence pass on this repo has not been done.',
  },
  {
    id: 'penny',
    name: 'Penny & aiframework',
    category: 'Platform · the shared runtime',
    hue: HUE.platform,
    live: 'aiportal.ca',
    oneLiner:
      'One agent platform underneath seven products — and a licensable, air-gappable version of it for professional-services firms.',
    pitch:
      'We didn’t build seven AI products; we built one platform and pointed it at seven markets. The same runtime powers career AI, event media, sports highlights, course creation and legal RAG. Then we packaged the platform itself: a firm installs it inside their own network, buys the vertical pack for their practice area, and gets an isolation guarantee that a CI job blocks the build over.',
    features: [
      'Shared runtime: orchestrator, RAG, document, internal-tools and MCP-client agents on PydanticAI',
      'Composable MCP tool platform — DB-backed registry with UI forms auto-generated from JSON schemas',
      'Citation-verified RAG: hybrid + RRF + bge-reranker-v2-m3 + page/char citations + NLI verification + abstain',
      'Messy-input ingestion: folder watcher, Tesseract 5 with de-skew/CLAHE and OCR-confidence flagging, DOCX, XLSX, EML',
      'Sellable versioned vertical packs — legal-criminal, mortgage, real-estate, wealth — plus an Ed25519 licence server',
      'Deploy anywhere: air-gapped (Ollama, Mac Studio) or SaaS (OpenAI/Anthropic) through one provider factory',
    ],
    metrics: [
      { value: '1,578', label: 'commits' },
      { value: '111', label: 'numbered SQL migrations' },
      { value: '7', label: 'products on one runtime' },
      { value: 'CI', label: 'blocking cross-tenant isolation gate' },
    ],
    stage: 'Core beta / internal-live · aiframework early alpha with real CI',
    stack: ['FastAPI', 'PydanticAI', 'Docling', 'Postgres + pgvector', 'Celery + Redis', 'Helm + Terraform', 'RLS + TOTP MFA'],
    straight:
      'aiframework is early alpha — 21/21 plan items still unchecked despite real tests and real CI. The *_COMPLETE.md convention is a verification habit, not a shipping claim.',
  },
  {
    id: 'fabric3d',
    name: 'Fabric3D — 3D assets',
    category: 'Generative media · assets',
    hue: HUE.media,
    oneLiner: 'Text or a photo in; a mesh Unreal Engine actually accepts out.',
    pitch:
      'Generative 3D produces meshes that look great in a viewer and fall apart in an engine. Fabric3D encodes what Unreal actually requires as a typed contract, checks every asset against it, repairs what fails, and publishes the comparison against naive cleanup. Everyone else sells generation; we sell acceptance.',
    features: [
      'Typed, machine-checkable Unreal conformance contract with automated repair and a published baseline comparison',
      'Image → 3D: TRELLIS.2, Hunyuan3D-2.1, TripoSR, InstantMesh',
      'Single-photo digital humans: SyncHuman, PSHuman, SMPL-X; Character Forge turns a photo into a MetaHuman',
      'Auto-rigging via UniRig; garment generation',
      'Guaranteed-manifold retopology and PBR rebake',
      'MCP server — agent-drivable asset production',
    ],
    metrics: [
      { value: 'UE5', label: 'conformance contract, machine-checked' },
      { value: 'GPU', label: 'verified on RTX 4090' },
    ],
    stage: 'Advanced prototype · GPU-verified',
    stack: ['Python', 'TRELLIS.2', 'Hunyuan3D-2.1', 'SMPL-X', 'UniRig', 'Unreal Engine 5', 'MCP'],
    straight: 'Advanced prototype on one local GPU; no cloud rendering fleet, no CI.',
  },
  {
    id: 'karak',
    name: 'Wujud / Karak — Film studio',
    category: 'Generative media · film',
    hue: HUE.media,
    oneLiner: 'Storyboard to finished film with a cast of MetaHumans that never sleep.',
    pitch:
      'A film studio’s fixed costs are cast, crew, sets and schedule. Karak has thirteen actors, six standing sets and a crew of agents — writer, director, DP, editor, critic — and it renders. Voices are cloned in-house, including Urdu, so it is not limited to English-language output.',
    features: [
      'Storyboard → voices → lip-sync → multicam → rendered MP4, headless on UE5 + MetaHuman',
      '13 MetaHumans across 6 standing sets',
      'Character Forge: photo → castable MetaHuman with a VLM casting spec and likeness gating',
      'Hand-rolled agent crew — writer, director, DP, cine, editor, critic, gate, bible, blocking',
      'Multilingual voice cloning at production scale (Higgs Audio v3 4B, incl. Urdu); Audio2Face lip-sync',
      'Stakes-routed LLM selection with Zod-typed calls, repair and safe in-character fallbacks',
    ],
    metrics: [
      { value: '13', label: 'MetaHumans' },
      { value: '6', label: 'standing sets' },
      { value: 'MP4', label: 'finished renders, not slideware' },
    ],
    stage: 'Working alpha · renders shipping',
    stack: ['UE 5.7/5.8 + MetaHuman', 'TypeScript + Zod', 'Claude Haiku/Sonnet/Opus routed by stakes', 'Higgs Audio v3', 'Supertonic TTS'],
    straight: 'Working alpha on one local GPU, no CI. No fine-tuning code exists anywhere in the Wujud tree despite prose mentions.',
  },
  {
    id: 'scener',
    name: 'Wujud / Scener — Motion capture',
    category: 'Generative media · performance',
    hue: HUE.media,
    oneLiner: 'One camera, one actor, a full MetaHuman performance — body, hands and 52-blendshape face.',
    pitch:
      'Motion capture means a stage, a suit and a budget. Scener needs a phone video. What makes it credible is that quality is measured rather than asserted: an eleven-clip benchmark with per-gate pass/fail, and gates that each exist because a specific bug shipped once.',
    features: [
      'Monocular video → full MetaHuman performance: body, per-phalanx hands, 52 ARKit face blendshapes',
      'One DSL between capture and engine — retarget any performance onto any MetaHuman',
      'Fully headless: Windows Unreal driven from WSL',
      'Measured, gated animation quality with a published 11-clip benchmark',
      'Framing gate that catches fabricated bodies; likeness gate; crash-regression harness',
      'QA gates in place of unit tests, each traceable to a shipped bug',
    ],
    metrics: [
      { value: '11.58 → 0.21', label: 'cm foot float' },
      { value: '4.9° → 1.6°', label: 'neck fidelity' },
      { value: '9/11', label: 'clips pass every gate' },
    ],
    stage: 'Late prototype · scorecard published',
    stack: ['GVHMR + WHAM', 'SMPL-X', 'MediaPipe', 'YOLO11n', 'torch cu121 / WSL', 'UE5 MetaHuman'],
    straight: 'Two of eleven benchmark clips still fail at least one gate, and we publish that rather than reporting the nine.',
  },
  {
    id: 'ibztech',
    name: 'Wujud / ibztech-studio — Game studio',
    category: 'Generative media · games',
    hue: HUE.media,
    oneLiner: 'A sentence becomes a shipped Unreal Engine game with an AI antagonist that directs the enemies live.',
    pitch:
      'Generative game tools produce assets. This produces the game — C++, because Blueprints are not generatable, and packaged, because a packaged executable is the only proof anyone accepts. Then a language model plays the antagonist live, watching you and directing the enemies, with a voice.',
    features: [
      'Premise → agent DAG → playable UE5 C++ first-person game',
      'Eight generated titles; one packaged executable',
      'Live LLM antagonist directing enemies during play, with a voice',
      'C++ generation by design — Blueprints rejected as non-generatable',
    ],
    metrics: [
      { value: '8', label: 'generated game titles' },
      { value: '.exe', label: 'packaged and playable' },
    ],
    stage: 'Playable alpha',
    stack: ['Unreal Engine 5 C++', 'MSVC', 'Agent DAG', 'Procedural DSP + SAPI voice'],
    straight: 'Playable alpha, single machine, no CI, no distribution.',
  },
  {
    id: 'hollow-lives',
    name: 'Wujud / hollow-lives — Simulated TV',
    category: 'Generative media · simulation',
    hue: HUE.media,
    oneLiner: 'A television show that runs by itself, forever.',
    pitch:
      'Not a generated episode — a running world. Characters act on their own clock, the director watches and decides what is worth shooting, and the renderer shoots it. Because the simulation and direction layers are engine-neutral by contract, the world outlives any engine we render it in.',
    features: [
      'Three layers: Sim Brain → Direction → Render, with layers 1–2 engine-neutral by contract and a test that enforces it',
      'Agents ticking in in-world minutes on a deterministic seeded clock and RNG',
      'Director and editor agents producing ShotScripts from simulation events',
      'Append-only event log; paired git-tag + UE-asset checkpoints for restorable state',
      'Safety floor, moderation throttle and fourth-wall guard as tested invariants',
    ],
    metrics: [
      { value: '71', label: 'Vitest suites' },
      { value: '8', label: 'packages' },
    ],
    stage: 'Parked alpha · resumable asset',
    stack: ['TypeScript + pnpm', 'Zod', 'Postgres 16 + pgvector', 'Drizzle', 'Unreal bridge'],
    straight:
      'Explicitly deprioritized in favour of Karak; listed as a capability proof and a resumable asset, not an active line.',
  },
  {
    id: 'commons',
    name: 'Commons — Plastic-free home',
    category: 'Venture · consumer goods',
    hue: HUE.venture,
    oneLiner: 'A home-goods brand whose argument is chemistry and whose proof is a lab report.',
    pitch:
      'Every “eco” brand runs on vibes and a green logo. Commons runs on a published standard: 114 distinct criteria, 60 mandatory per SKU, 17 disqualifying with no cure window. When a product fails, it keeps its register number and its reason — because a register that renumbers is not a register. Two specimens are currently withdrawn and both are named on the site. The withdrawals are the marketing.',
    features: [
      'CS-1 certification standard: 114 distinct criteria in three tiers, 60 mandatory per SKU, 17 disqualifiers',
      '72-specimen catalogue with a permanent withdrawn register — numbers are never reused',
      'Per-SKU realization dossier (76 entries) as a printable engineering document',
      'A platform strategy document that audits and corrects nine of its own v1.0 claims in Part 0',
      'Companion Tayyib line — same standard, same supply chain, one added certification layer',
      'Seven-route document system with real print geometry: letter sheets, running heads and feet',
    ],
    metrics: [
      { value: '114', label: 'CS-1 criteria, 60 per SKU' },
      { value: '72', label: 'active specimens + 2 withdrawn' },
      { value: '9', label: 'self-audited corrections in v2.0' },
    ],
    stage: 'Brand, standard and document set complete · pre-manufacture',
    stack: ['Vite 7 + React 19', 'Tailwind v4', 'shadcn/ui', 'react-markdown', 'Print CSS'],
    straight:
      'This is brand, standard and document set — not manufacturing. Phase 1 capital was restated upward to $68–173K once certification costs were verified, and margins down to 9–25% post-ad.',
  },
  {
    id: 'biosteel',
    name: 'BioSteel Teams',
    category: 'Client delivery',
    hue: HUE.client,
    live: 'App Store · Google Play',
    oneLiner: 'Shipped to both app stores, for a client, on a deadline.',
    pitch:
      'Everything else in this portfolio is ours. This one was someone else’s brief, someone else’s brand and someone else’s timeline, and it is in the app stores. That is the line item that says we can also deliver.',
    features: [
      'Native mobile delivery on iOS and Android',
      'Dedicated Teams API backend',
      'Shipped to both app stores',
    ],
    metrics: [
      { value: '2', label: 'app stores, live' },
    ],
    stage: 'Shipped',
    stack: ['Mobile (iOS + Android)', 'Teams API backend'],
    straight: 'Client work; not surveyed at code level.',
  },
];

/* The portfolio map, grouped the way an investor reads it: what ships, what sells, what compounds. */
export const MAP: { band: string; note: string; items: { name: string; what: string; hue: string }[] }[] = [
  {
    band: 'In production or live',
    note: 'Deployed, monitored, taking real users',
    items: [
      { name: 'LensEvents AI', what: 'Face-aware event media on AWS RDS + S3', hue: HUE.media },
      { name: 'Lumha Creator', what: 'AI course-creator SaaS at lumha.com', hue: HUE.media },
      { name: 'aiportal.ca', what: 'Penny core, internal-live', hue: HUE.platform },
      { name: 'AutoArchitect', what: 'autoarchitect.ai live; engine pre-release', hue: HUE.platform },
      { name: 'BioSteel Teams', what: 'Client mobile app, both app stores', hue: HUE.client },
    ],
  },
  {
    band: 'Demo-complete — sellable now',
    note: 'Full pipelines running end to end on seeded real data',
    items: [
      { name: 'ZakhuSign', what: 'AI-native e-signature, PAdES-sealed', hue: HUE.vertical },
      { name: 'Klipki — Playback', what: 'AI sports video intelligence, real CI', hue: HUE.media },
      { name: 'Lumha Events', what: 'Visual Intelligence OS, 145/145 e2e', hue: HUE.media },
      { name: 'AI Portal', what: 'Grounded legal RAG with eval gates', hue: HUE.platform },
      { name: 'Capsule', what: 'Capability bundles, measured on real specs', hue: HUE.infra },
      { name: 'CareerBeyond AI', what: 'Career platform, 60+ E2E', hue: HUE.vertical },
    ],
  },
  {
    band: 'Working alpha',
    note: 'Real product surface, known gaps, named deferrals',
    items: [
      { name: 'Zakhu', what: 'Brokerage OS / agent swarm', hue: HUE.vertical },
      { name: 'Lugash', what: 'Marketing OS with a live agent loop', hue: HUE.vertical },
      { name: 'Karak', what: 'AI film studio, renders shipping', hue: HUE.media },
      { name: 'Scener', what: 'Video → MetaHuman performance', hue: HUE.media },
      { name: 'ibztech-studio', what: 'Premise → playable UE5 game', hue: HUE.media },
      { name: 'Fabric3D', what: 'Engine-ready assets + digital humans', hue: HUE.media },
      { name: 'aiframework', what: 'Private multi-tenant AI workspace', hue: HUE.platform },
      { name: 'Commons', what: 'Plastic-free standard + catalogue', hue: HUE.venture },
    ],
  },
  {
    band: 'Early / parked',
    note: 'Structure in place, deliberately not the current focus',
    items: [
      { name: 'Lantern', what: 'Self-hosted search & acquisition API', hue: HUE.infra },
      { name: 'hollow-lives', what: 'Always-on simulated TV show', hue: HUE.media },
    ],
  },
];

export const CLUSTERS = [
  {
    n: '01',
    title: 'Agentic systems from first principles',
    body: 'No LangGraph, no CrewAI, anywhere. Every project ships its own agent loop with deterministic control: AutoArchitect’s frozen six-role Conductor, Lugash’s in-process nine-phase Loop, Zakhu’s decision-card swarm, Wujud’s writer/director/DP/editor/critic crews. Structured outputs everywhere via Zod and Pydantic with validate-and-repair.',
    proof: 'MCP on all three sides — servers, graded client allowlists, and a compiler for what should be on the wire.',
  },
  {
    n: '02',
    title: 'Grounded, evaluated RAG',
    body: 'pgvector hybrid vector + BM25 fused with Reciprocal Rank Fusion, contextual chunking, cross-encoder reranking, page and character-offset citations, NLI citation verification, cross-model-family groundedness verifiers, abstain-on-low-confidence.',
    proof: 'Published eval harnesses with release gates: hallucination ≤2%, 0/10 injections obeyed, benchmarked against Stanford HAI legal-AI numbers.',
  },
  {
    n: '03',
    title: 'Sovereign inference',
    body: 'A self-hosted OpenAI-compatible gateway serves gpt-oss:120b, qwen3.5, qwen3-vl:235b, deepseek-v4, glm-5.2, minimax-m3. Frontier lanes route by stakes. Every recent product ships a no-key deterministic path, so the product is honest and alive without a vendor.',
    proof: 'The same products run air-gapped on an RTX 4090 or a Mac Studio, or as SaaS, through one provider factory.',
  },
  {
    n: '04',
    title: 'Digital humans on Unreal Engine',
    body: 'UE 5.7/5.8 + MetaHuman driven headlessly from WSL. Image→3D, single-photo humans, auto-rigging, guaranteed-manifold retopology with a machine-checkable conformance contract. Monocular video → full MetaHuman performance. In-house multilingual voice cloning.',
    proof: 'A capability almost no agency has: 13 MetaHumans, 6 sets, a published mocap benchmark, and a packaged playable game.',
  },
  {
    n: '05',
    title: 'Computer vision & media pipelines',
    body: 'SigLIP-2, AuraFace, RF-DETR, PaddleOCR, Qwen3-VL, ByteTrack, Depth-Anything-V2, Real-ESRGAN, Docling + Tesseract with de-skew and confidence flagging, ffmpeg reels and EDL-reproducible renders.',
    proof: 'Licence-clean model selection with written BOMs — and a CI licence gate that runs first and alone.',
  },
  {
    n: '06',
    title: 'Full-stack product engineering',
    body: 'FastAPI, Fastify, React 18–19, Tailwind v4, Drizzle and SQLAlchemy, Postgres + pgvector, Redis/Valkey, S3. Stripe subscription state machines, Sentry with PII scrubbing, Docker, Kubernetes with gVisor, Helm, Terraform, signed multi-arch images.',
    proof: 'Security as a feature: RLS, HMAC-signed QR and webhooks, TOTP MFA, hash-chained audit logs, PAdES sealing, fail-closed binds.',
  },
  {
    n: '07',
    title: 'How we work',
    body: 'Spec-driven to the point of written constitutions and PRD/BDD. Agent-driven development with CLAUDE.md, AGENTS.md and per-module SOUL.md. QA gates born from bugs that shipped. Benchmarks published before the numbers exist.',
    proof: 'Every repo carries its intent — a new engineer inherits the reasoning, not just the code.',
  },
];

export const THESES = [
  {
    n: 'I',
    title: 'Structured truth before generation',
    body: 'Playback’s event graph. ZakhuSign’s terms object. Lugash’s World Model. AutoArchitect’s DomainT graph. AI Portal’s citations. In every case the model proposes into a typed, auditable structure, and the product is generated from the structure — never from raw model output.',
    why: 'This is what makes the outputs defensible in regulated and money-bearing domains.',
  },
  {
    n: 'II',
    title: 'Sovereignty is a feature, not a fallback',
    body: 'Every product runs on self-hosted inference and degrades to a deterministic path with no vendor key at all. ZakhuSign’s rules engine. Lugash’s heuristics. Lumha’s mock provider. Zakhu’s cited stub.',
    why: 'That sells to law firms, brokerages and any buyer with a data-residency problem — and it caps COGS.',
  },
  {
    n: 'III',
    title: 'We build the layer below the layer',
    body: 'Capsule compiles what should be on the MCP wire. Lantern is self-hosted search and acquisition. Both are infrastructure the rest of the portfolio consumes.',
    why: 'And the rest of the portfolio is the design-partner base that proves them.',
  },
];

export const NUMBERS: Metric[] = [
  { value: '6', label: 'Proof evidence lanes, replayable as receipts — AutoArchitect' },
  { value: '2,007', label: 'API operations compiled, zero id collisions' },
  { value: '85.1%', label: 'cold token reduction vs raw-doc ingestion' },
  { value: '0/10', label: 'prompt injections obeyed' },
  { value: '≤2%', label: 'hallucination release gate' },
  { value: '145/145', label: 'end-to-end scenarios, Lumha Events' },
  { value: '259', label: 'connector catalogue' },
  { value: '≤$2.50', label: 'per game-hour analysis budget' },
  { value: '11.58→0.21', label: 'cm foot float, 11-clip mocap benchmark' },
  { value: '114', label: 'CS-1 criteria over 72 specimens' },
  { value: '1,578', label: 'commits on the shared Penny platform' },
  { value: '16', label: 'MCP tools on one pipeline' },
];

export const RISKS = [
  {
    risk: 'No trained domain models yet',
    where: 'Klipki — Playback',
    answer:
      'The detector runs COCO-pretrained weights, so confidences sit below the packs’ review thresholds by design and the product withholds rather than guesses. The training path is wired: Unsloth core, PEFT, and consent-gated example collection.',
  },
  {
    risk: 'Committed dev secrets, no CI',
    where: 'Zakhu · Lumha · Lugash · ZakhuSign',
    answer:
      'Known and written down. Key rotation is gated “before any public launch” in Zakhu’s own README. CI is a day-one item per repo. Three of the strongest trees already have it.',
  },
  {
    risk: 'Licensed third-party forms',
    where: 'ZakhuSign',
    answer:
      'OREA/TRREB layouts are never shipped. The operator uploads their own licensed PDFs and the platform learns the field map. Legally clean by construction, not by policy.',
  },
  {
    risk: 'A single local GPU',
    where: 'Wujud · Fabric3D · Klipki — Playback',
    answer:
      'One RTX 4090, 24 GB. Everything is written to scale out — provider registries, 4-bit quantisation, a vLLM extra — but it has not been run on a fleet.',
  },
  {
    risk: 'Concentration on one builder',
    where: 'Portfolio-wide',
    answer:
      'Spec-driven and agent-driven development is the mitigation. Every repo carries its PRD, architecture contract, invariants and CLAUDE.md, so a new engineer inherits intent rather than archaeology.',
  },
];

export const slides: Slide[] = [
  { kind: 'cover' },
  { kind: 'map' },
  { kind: 'theses' },
  { kind: 'clusters' },
  ...projects.map((p, i) => ({ ...p, kind: 'project' as const, index: i + 1 })),
  { kind: 'numbers' },
  { kind: 'risks' },
  { kind: 'close' },
];

export const PROJECT_COUNT = projects.length;

export function slideTitle(s: Slide): string {
  switch (s.kind) {
    case 'cover':
      return 'eXonstech';
    case 'map':
      return 'Portfolio at a glance';
    case 'theses':
      return 'Three theses';
    case 'clusters':
      return 'What we demonstrably do';
    case 'numbers':
      return 'Numbers we can defend';
    case 'risks':
      return 'Straight talk';
    case 'close':
      return 'What we are raising for';
    default:
      return s.name;
  }
}
